import {
  UberFilterGroup_default,
  getEffectiveFilterType,
  getUniqueNumericOptions
} from "./chunk-ZCQUQSLO.mjs";
import {
  scrollbarSx_default
} from "./chunk-GD6E42CP.mjs";
import "./chunk-JYXW2UNX.mjs";
import {
  ShareLinkButton
} from "./chunk-BARDMAFJ.mjs";

// src/FiltersWithDropdown.tsx
import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles/index.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box/index.js";
import Typography from "@mui/material/Typography/index.js";
import Breadcrumbs from "@mui/material/Breadcrumbs/index.js";
import Link from "@mui/material/Link/index.js";
import TextField from "@mui/material/TextField/index.js";
import Chip from "@mui/material/Chip/index.js";
import SearchIcon from "@mui/icons-material/esm/Search.js";
import CloseIcon from "@mui/icons-material/esm/Close.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var findLabelForRawValue = (numericOptions, rawValue) => {
  if (numericOptions.length === 0) return rawValue;
  let closest = numericOptions[0];
  let closestDiff = Math.abs(closest.value - rawValue);
  numericOptions.forEach((option) => {
    const diff = Math.abs(option.value - rawValue);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = option;
    }
  });
  return closest.label;
};
var renderFilterName = (text) => {
  return String(text).split(/(<sub>.*?<\/sub>)/g).map((part, index) => {
    if (part.startsWith("<sub>") && part.endsWith("</sub>")) {
      return /* @__PURE__ */ jsx("sub", { children: part.replace(/<\/?sub>/g, "") }, index);
    }
    return part;
  });
};
var FiltersWithDropdown = ({
  name,
  handleSearchChange,
  splashImage,
  siblings,
  categories,
  allCategoryData,
  filters,
  handleFilterApply,
  partNumber,
  itemcode,
  searchInput = "",
  initialFilters = []
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const searchFieldLabel = t("searchBoxHero.searchBoxLabel") !== "searchBoxHero.searchBoxLabel" ? t("searchBoxHero.searchBoxLabel") : t("searchBoxHero.serachBoxLabel");
  const categoryId = categories.find((category) => category.name === name)?.id;
  const parentCategory = categories.find(
    (category) => category.childrenIds?.some((child) => child.id === categoryId)
  );
  const [selectedCategory, setSelectedCategory] = useState(name);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const resetFilters = () => {
    setSelectedFilters([]);
    handleFilterApply([]);
  };
  useEffect(() => {
    setSelectedCategory(name);
    if (itemcode) {
      const itemcodeFilter = {
        columnName: "ItemCode",
        value: itemcode,
        operator: "eq"
      };
      setSelectedFilters([itemcodeFilter]);
      handleFilterApply([itemcodeFilter]);
    } else if (initialFilters.length > 0) {
      setSelectedFilters(initialFilters);
      handleFilterApply(initialFilters);
    } else {
      resetFilters();
    }
    if (partNumber) {
      handleApply([
        {
          columnName: "PartNumber",
          value: partNumber,
          operator: "eq"
        }
      ]);
    }
  }, [name, itemcode]);
  const getFilterLabel = (filter) => {
    const columnName = filter.columnName.split(".").pop();
    if (columnName === "ItemCode") {
      return renderFilterName(`Item Code: ${filter.value}`);
    }
    const controlColumn = columnName === "Certificates" ? "label" : columnName;
    const control = filters.filterGroups.flatMap((group) => group.controls).find((ctrl) => ctrl.column === controlColumn);
    if (!control) {
      return renderFilterName(`${columnName}: ${filter.value}`);
    }
    const controlUnit = control.unit;
    const unit = controlUnit ? ` ${controlUnit}` : "";
    let displayValue;
    if (getEffectiveFilterType(allCategoryData, control) === "checkboxlist") {
      displayValue = Array.isArray(filter.value) ? filter.value.join(", ") : filter.value;
    } else {
      const numericOptions = getUniqueNumericOptions(
        allCategoryData,
        control.column
      );
      displayValue = Array.isArray(filter.value) ? filter.value.map((v) => findLabelForRawValue(numericOptions, v)).join(", ") : findLabelForRawValue(numericOptions, filter.value);
    }
    let operatorSymbol = "";
    switch (filter.operator) {
      case "gte":
        operatorSymbol = "\u2265";
        break;
      case "lte":
        operatorSymbol = "\u2264";
        break;
      case "eq":
        operatorSymbol = "=";
        break;
      case "in":
        operatorSymbol = "\u2208";
        break;
      default:
        operatorSymbol = filter.operator;
    }
    return renderFilterName(
      `${columnName} ${operatorSymbol} ${displayValue}${unit}`
    );
  };
  const removeFilter = (filter) => {
    const newFilters = selectedFilters.filter(
      (item) => item.columnName !== filter.columnName || item.value !== filter.value || item.operator !== filter.operator
    );
    setSelectedFilters(newFilters);
    handleFilterApply(newFilters);
    if (filter.columnName === "Part Number" || filter.columnName === "ItemCode") {
      navigate(`/products/${name}/list`);
    }
  };
  const clearAllFilters = () => {
    setSelectedFilters([]);
    handleFilterApply([]);
    navigate(`/products/${name}/list`);
  };
  const handleApply = (newFilters, pruneKeys = []) => {
    const remainingFilters = selectedFilters.filter(
      (filter) => !pruneKeys.some(
        (key) => key.columnName === filter.columnName && key.operator === filter.operator
      )
    );
    const updatedFilters = [...remainingFilters];
    newFilters.forEach((newFilter) => {
      const existingFilterIndex = updatedFilters.findIndex(
        (filter) => filter.columnName === newFilter.columnName && filter.operator === newFilter.operator
      );
      if (existingFilterIndex > -1) {
        updatedFilters[existingFilterIndex] = newFilter;
      } else {
        updatedFilters.push(newFilter);
      }
    });
    setSelectedFilters(updatedFilters);
    handleFilterApply(updatedFilters);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Breadcrumbs, { "aria-label": "breadcrumb", children: [
      /* @__PURE__ */ jsx(
        Link,
        {
          underline: "hover",
          color: "inherit",
          onClick: () => navigate("/products"),
          href: "/products",
          style: { cursor: "pointer" },
          children: "Products"
        }
      ),
      parentCategory ? /* @__PURE__ */ jsx(
        Link,
        {
          underline: "hover",
          color: "inherit",
          onClick: () => navigate(
            `/products/${parentCategory.childrenIds?.map((child) => child.id).join(",")}`
          ),
          href: `/products/${parentCategory.childrenIds?.map((child) => child.id).join(",")}`,
          style: { cursor: "pointer" },
          children: parentCategory.name
        }
      ) : /* @__PURE__ */ jsx(Typography, { color: "text.secondary", children: "Unknown Category" }),
      /* @__PURE__ */ jsx(Typography, { color: "text.primary", children: name })
    ] }),
    /* @__PURE__ */ jsx(
      Box,
      {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        sx: {
          paddingTop: "2%",
          width: "100%",
          height: "auto",
          maxHeight: "30vh",
          objectFit: "cover"
        },
        children: /* @__PURE__ */ jsx(
          Box,
          {
            component: "img",
            sx: {
              width: "auto",
              height: "auto",
              maxHeight: "23vh",
              objectFit: "cover"
            },
            src: splashImage,
            alt: "category.image"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Box,
      {
        display: "flex",
        justifyContent: "center",
        marginBottom: 4,
        marginTop: 4,
        sx: {
          flexWrap: "wrap",
          gap: 2
        },
        children: siblings.map((sibling, index) => /* @__PURE__ */ jsx(
          Link,
          {
            component: "button",
            variant: "body2",
            onClick: () => navigate(`/products/${sibling.name}/list`),
            sx: {
              textDecoration: sibling.name === selectedCategory ? "underline" : "none",
              color: sibling.name === selectedCategory ? theme.palette.primary.main : theme.palette.text.secondary,
              fontWeight: sibling.name === selectedCategory ? "bold" : "normal",
              fontSize: "1.1rem",
              padding: "8px 16px",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              backgroundColor: sibling.name === selectedCategory ? "rgba(25, 118, 210, 0.08)" : "transparent",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.12)"
              }
            },
            children: sibling.name
          },
          index
        ))
      }
    ),
    /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 2,
          backgroundColor: "white",
          borderRadius: "12px",
          padding: 3,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
        },
        children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 1
              },
              children: [
                /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: "subtitle1",
                    fontWeight: 600,
                    sx: { color: "text.primary" },
                    children: t("searchBoxHero.filterOptionsLabel")
                  }
                ),
                /* @__PURE__ */ jsx(ShareLinkButton, { tooltip: "Copy link to these results" })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              sx: {
                width: "100%",
                marginBottom: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "white",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }
              },
              children: /* @__PURE__ */ jsx(
                TextField,
                {
                  fullWidth: true,
                  type: "search",
                  value: searchInput,
                  onChange: (event) => handleSearchChange(event, event.target.value),
                  label: /* @__PURE__ */ jsxs(
                    Typography,
                    {
                      sx: {
                        display: "flex",
                        alignItems: "center",
                        color: "text.secondary",
                        fontWeight: 500
                      },
                      children: [
                        /* @__PURE__ */ jsx(SearchIcon, { sx: { marginRight: "0.5rem" } }),
                        searchFieldLabel
                      ]
                    }
                  )
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              sx: {
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                maxHeight: "200px",
                overflowY: "auto",
                paddingRight: 1,
                ...scrollbarSx_default
              },
              children: filters && filters.filterGroups.map((filterGroup) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
                UberFilterGroup_default,
                {
                  optionsData: allCategoryData,
                  filterGroup,
                  selectedFilters,
                  callback: handleApply
                }
              ) }, filterGroup.name))
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          padding: 2,
          backgroundColor: "rgba(25, 118, 210, 0.04)",
          borderRadius: "12px",
          marginBottom: 2
        },
        children: [
          selectedFilters.map((filter, index) => /* @__PURE__ */ jsx(
            Chip,
            {
              label: getFilterLabel(filter),
              onDelete: () => removeFilter(filter),
              deleteIcon: /* @__PURE__ */ jsx(CloseIcon, {}),
              sx: {
                backgroundColor: "white",
                "& .MuiChip-deleteIcon": {
                  color: "text.secondary",
                  "&:hover": {
                    color: "error.main"
                  }
                }
              }
            },
            index
          )),
          selectedFilters.length > 0 && /* @__PURE__ */ jsx(
            Chip,
            {
              label: "Clear all",
              onClick: clearAllFilters,
              variant: "outlined",
              sx: {
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)"
                }
              }
            }
          )
        ]
      }
    )
  ] });
};
export {
  FiltersWithDropdown
};
